import { type ReactNode } from 'react';
import { customName, splitCustom, type CustomEmoji } from '@engineer-os/chat';
import { useLocale } from '../../state/useLocale';

/**
 * The pictures for the emoji this product drew for itself.
 *
 * The roster — which shortcodes exist, what they are called, what they are
 * found by — is in the protocol package, because the Worker has to agree with
 * the tab about what a string means. What is here is the half only a browser
 * needs: fifty-eight drawings on a 24-unit grid, and twelve stickers on a
 * 48-unit one.
 *
 * They are SVG rather than images, and inline rather than files, and both of
 * those are the same decision. A message list that draws six of them is six
 * more requests if they are files, and a 20px PNG of a rounded rectangle is a
 * blurry rounded rectangle on every display made in the last decade. Inline
 * they cost nothing, they scale from the 18px in a line of text to the 34px of
 * a message that is only emoji without a second asset, and they are drawn in
 * `currentColor`, so the one that sits in a dimmed pending message dims with
 * it. Nothing here is fetched, which also means nothing here fails to arrive:
 * the room works the same on the first paint as on the thousandth, and inside
 * the emulator's offline page as on the live site.
 *
 * The palette is the product's own — ink, the ground behind it, and Signal
 * Green — instead of the full-colour cartoons Unicode ships. That is the point
 * of them. A 🎉 in a room is a picture somebody else drew; `:shipit:` is ours,
 * and it looks like the thing it came out of. Green is used the way the rest
 * of the system uses it, on the part that is live or good: the deploy that is
 * moving, the suite that is passing, the plaster on the fire. Nothing that
 * failed is red, because there is no red here and never was — a broken build
 * is a hollow ring with a cross in it, which is the same way the product draws
 * every other fact it does not want to shout about.
 *
 * Three of them move: the prompt's caret blinks, the deploy's bar fills, the
 * party caret jumps. A fourth joins them from the stickers — `itsalive`'s
 * caret, on the same `ce-blink` — and that is the whole list. They are the
 * only things in a message list allowed to animate forever, they are small,
 * and they all stop under `prefers-reduced-motion` (emoji-custom.css).
 *
 * A roster entry with no drawing here renders as its own shortcode — the text
 * that was sent. That is the same thing an old tab shows, and it is a legible
 * failure rather than an empty box.
 *
 * There are two tables, and the reason is the canvas rather than the subject.
 * `ART` is drawn on 24 units for things the size of a word; `STICKER_ART` is
 * drawn on 48 for the twelve that are sent instead of a sentence. A sticker is
 * not a bigger emoji — it is a picture with room in it for a ground line, a
 * second object, a before and an after — and none of that survives being
 * authored on a grid where one unit is most of a pixel.
 */

/* ── The shared parts ───────────────────────────────────────────────────── */

/**
 * The chassis every expression is built on: a screen.
 *
 * The Expressions group is the one place this product draws a face, and a
 * yellow circle would have been someone else's face. These are terminals with
 * eyes — the thing the learner has been staring at for the whole level — which
 * is both distinct from every Unicode smiley at 18px and the only face this
 * product has any business having.
 */
const SCREEN = <rect x="2.6" y="4.6" width="18.8" height="14.8" rx="3.2" />;

/** Eyes, as two dots, where a face's eyes go. */
const EYES = (
  <g className="ink">
    <circle cx="8.6" cy="11" r="1.15" />
    <circle cx="15.4" cy="11" r="1.15" />
  </g>
);

/* ── The drawings ───────────────────────────────────────────────────────── */

/**
 * Shortcode to artwork. The `<svg>` around it sets the box, the stroke and the
 * joins, so a drawing is only the shapes.
 */
const ART: Record<string, ReactNode> = {
  /* --- EngineerOS ------------------------------------------------------- */

  // A crate with somewhere to be.
  shipit: (
    <>
      <g className="dim">
        <path d="M1.2 7.5h3.6" />
        <path d="M0.8 12h4" />
        <path d="M2 16.5h2.8" />
      </g>
      <rect x="7.8" y="4.6" width="13.4" height="14.8" rx="2.2" />
      <g className="g">
        <path d="M14.5 16.6V9.2" />
        <path d="M11.3 12.4l3.2-3.2 3.2 3.2" />
      </g>
    </>
  ),

  // A bar that keeps filling. The one emoji that is a status rather than a
  // reaction: it is what you send when someone asks whether it is out yet.
  deploying: (
    <>
      <rect x="2.4" y="8.4" width="19.2" height="7.2" rx="3.6" />
      <g className="dim">
        <path d="M9 9.9v4.2" />
        <path d="M13 9.9v4.2" />
        <path d="M17 9.9v4.2" />
      </g>
      <rect className="gf ce-fill" x="4.6" y="10.2" width="14.8" height="3.6" rx="1.8" />
    </>
  ),

  // Three green commits and a suite that agrees with them.
  greenbuild: (
    <>
      <path className="dim" d="M3.4 12h9" />
      <g className="gf">
        <circle cx="3.4" cy="12" r="1.9" />
        <circle cx="8.6" cy="12" r="1.9" />
        <circle cx="16.6" cy="12" r="5.4" />
      </g>
      <path className="punch" d="M14.2 12.1l1.8 1.9 3.2-3.9" />
    </>
  ),

  // The same picture with the last node hollow. Not red: a failure is a fact,
  // and the system draws facts in ink (brand.css §4).
  redbuild: (
    <>
      <path className="dim" d="M3.4 12h9" strokeDasharray="2 2.4" />
      <g className="ink">
        <circle cx="3.4" cy="12" r="1.9" />
        <circle cx="8.6" cy="12" r="1.9" />
      </g>
      <circle cx="16.6" cy="12" r="5.2" />
      <path d="M14.7 10.1l3.8 3.8M18.5 10.1l-3.8 3.8" />
    </>
  ),

  // Two lines that became one, and the commit where it happened.
  merged: (
    <>
      <path d="M7 6.6v10.8" />
      <path d="M17 6.6v1.6c0 2.4-2 3.8-5 3.8H9.4" />
      <circle cx="7" cy="4.7" r="1.9" />
      <circle cx="17" cy="4.7" r="1.9" />
      <circle className="gf" cx="7" cy="12" r="2.5" />
    </>
  ),

  // One commit off somebody else's branch, and only that one.
  cherrypick: (
    <>
      <path className="dim" d="M2.6 4.6h18.8" />
      <path d="M9 5.2c-1.6 2.8-2.2 4.6-2 6.6" />
      <path d="M15.4 5.2c1.4 2.8 1.4 4.8 1 6.6" />
      <circle cx="6.6" cy="15" r="3" />
      <circle className="gf" cx="16.2" cy="15.4" r="3.4" />
    </>
  ),

  // Not finished, not lost. Somewhere to put the afternoon.
  stash: (
    <>
      <circle className="gf" cx="12" cy="4.6" r="2.2" />
      <path className="dim" d="M12 7.6v2" />
      <path d="M2.6 10.6h18.8" />
      <rect x="4" y="13" width="16" height="7.4" rx="1.8" />
      <path className="dim" d="M9.4 16.6h5.2" />
    </>
  ),

  // Halve the history until the commit confesses.
  bisect: (
    <>
      <path className="dim" d="M2.6 15.4h18.8" />
      <circle className="gf" cx="3.4" cy="15.4" r="1.7" />
      <circle className="ink" cx="9" cy="15.4" r="1.5" />
      <circle cx="14.6" cy="15.4" r="1.5" />
      <circle cx="20.6" cy="15.4" r="1.7" />
      <path d="M19.4 14.2l2.4 2.4M21.8 14.2l-2.4 2.4" strokeWidth="1.2" />
      <path className="g" d="M9 5.4v4.8" />
      <path className="g" d="M6.8 8.2L9 10.4l2.2-2.2" />
    </>
  ),

  // Six commits called "wip" and one that says what happened.
  squash: (
    <>
      <path d="M3.4 4.6h17.2" />
      <g className="dim">
        <circle cx="7" cy="10" r="1.8" />
        <circle cx="12" cy="10" r="1.8" />
        <circle cx="17" cy="10" r="1.8" />
      </g>
      <path className="dim" d="M12 13.4v1.4" />
      <rect className="gf" x="6.4" y="16.4" width="11.2" height="4" rx="2" />
    </>
  ),

  // Two changes arriving at the same line, and the seam between them.
  conflict: (
    <>
      <g className="dim">
        <path d="M1.6 12h4.6" />
        <path d="M4.4 9.8l2.2 2.2-2.2 2.2" />
        <path d="M22.4 12h-4.6" />
        <path d="M19.6 9.8l-2.2 2.2 2.2 2.2" />
      </g>
      <path d="M13 2.6l-2 3.2 2 3.1-2 3.1 2 3.1-2 3.3" />
    </>
  ),

  // The arrow that does not ask what was there before.
  forcepush: (
    <>
      <path d="M20.6 4.6v14.8" />
      <path d="M2.6 14.4h13" />
      <path d="M12.6 11.4l3.2 3-3.2 3" />
      <path className="g" d="M9.6 2.6L6 8h2.8l-1.6 3.8" />
    </>
  ),

  // Back to the one that worked.
  rollback: (
    <>
      <path d="M4.75 8.6A8 8 0 1 1 4.75 15.4" />
      <path d="M8.2 7.9L4.75 8.6l1.4 3.3" />
      <circle className="gf" cx="12" cy="12" r="2.4" />
    </>
  ),

  // A plaster on a cracked screen, applied by someone who is going to write
  // the real fix tomorrow.
  hotfix: (
    <>
      <rect className="dim" x="2.6" y="4.6" width="18.8" height="14.8" rx="2.6" />
      <path className="dim" d="M9.4 4.6l1.8 4.6-3 1.8 2.8 3.6-1.8 4.8" />
      <g className="g" transform="rotate(-32 12 12)">
        <rect x="3.4" y="9.2" width="17.2" height="5.6" rx="2.8" />
        <path d="M9.2 9.2v5.6M14.8 9.2v5.6" />
      </g>
    </>
  ),

  // It passed the last four times.
  flaky: (
    <>
      <path d="M8.4 3.2h7.2" />
      <path d="M9.4 3.2v9.6a2.6 2.6 0 0 0 5.2 0V3.2" />
      <path className="gf ce-slosh" d="M9.4 12h5.2v.8a2.6 2.6 0 0 1-5.2 0z" />
      <g className="dim">
        <path d="M18.6 8.4c1.2 2.2 1.2 5 0 7.2" />
        <path d="M5.4 8.4c-1.2 2.2-1.2 5 0 7.2" />
      </g>
    </>
  ),

  // The whole of a good review.
  lgtm: (
    <g className="g" transform="rotate(-7 12 12)">
      <rect x="1.8" y="7" width="20.4" height="10" rx="2.4" />
      <text x="12" y="14.6" textLength="14.4" lengthAdjust="spacingAndGlyphs">
        LGTM
      </text>
    </g>
  ),

  // Not a blocker. Still a comment.
  nit: (
    <>
      <g transform="rotate(-6 12 12)">
        <rect x="4.4" y="9" width="15.2" height="7.6" rx="2.2" />
        <text x="12" y="14.6" textLength="8.6" lengthAdjust="spacingAndGlyphs">
          nit
        </text>
      </g>
      <circle className="gf" cx="19.4" cy="6.2" r="1.6" />
    </>
  ),

  // The stamp that means "read it, do not review it". Dashed, because the
  // thing inside it is not finished either.
  wip: (
    <g transform="rotate(6 12 12)">
      <rect x="2.4" y="7.4" width="19.2" height="9.2" rx="2.2" strokeDasharray="2.6 2.2" />
      <text x="12" y="15.2" textLength="12.4" lengthAdjust="spacingAndGlyphs">
        WIP
      </text>
    </g>
  ),

  // The product's own mark, at emoji size: a chevron and a live caret.
  prompt: (
    <>
      <path d="M7 8.5L11.5 12L7 15.5" strokeWidth="2.2" />
      <rect className="gf ce-blink" x="14.5" y="7.5" width="4" height="9" />
    </>
  ),

  // The key with the crown on it. Everything after this line is your own
  // fault, which is most of what root means.
  sudo: (
    <>
      <path className="gf" d="M4.6 7.4l1.4-3.6 2 2 2-2.4 2 2.4 2-2 1.4 3.6z" />
      <circle cx="8.4" cy="14.6" r="4.2" />
      <path d="M12.4 14.6h8.2" />
      <path d="M17.4 14.6v3.2" />
      <path d="M20.4 14.6v2.6" />
    </>
  ),

  // The machine the whole product runs inside, with its light on.
  thebox: (
    <>
      <rect x="2.6" y="4.6" width="18.8" height="12.8" rx="2.2" />
      <g className="dim">
        <path d="M6 9h5.6" />
        <path d="M6 12.2h7.6" />
        <path d="M12 17.4v3" />
      </g>
      <circle className="gf" cx="18" cy="14.4" r="1.4" />
      <path d="M8.4 20.4h7.2" />
    </>
  ),

  // There is no place like it.
  localhost: (
    <>
      <path d="M2.4 11.4L12 4l9.6 7.4" />
      <path d="M5.2 12.4v7.6h13.6v-7.6" />
      <path d="M8.2 14.6l1.8 1.8-1.8 1.8" />
      <rect className="gf ce-blink" x="12.4" y="14.4" width="3" height="4" />
    </>
  ),

  // 03:00. It is always 03:00.
  pager: (
    <>
      <rect x="4" y="5.4" width="10.4" height="13.2" rx="2.2" />
      <g className="dim">
        <path d="M6.6 8.8h5.2" />
        <path d="M6.6 11.4h3.4" />
      </g>
      <circle className="ink" cx="9.2" cy="15.4" r="1.3" />
      <g className="g">
        <path d="M17.2 8.6a4.8 4.8 0 0 1 0 6.8" />
        <path d="M19.8 6a8.4 8.4 0 0 1 0 12" />
      </g>
    </>
  ),

  // Everyone agrees you should not. Somebody always does.
  shipfriday: (
    <>
      <path d="M7.4 2.6v3.2" />
      <path d="M16.6 2.6v3.2" />
      <rect x="2.6" y="4.6" width="18.8" height="15.8" rx="2.2" />
      <path className="dim" d="M2.6 9.4h18.8" />
      <text x="11.4" y="16.6" textLength="11.6" lengthAdjust="spacingAndGlyphs">
        FRI
      </text>
      <path
        className="gf"
        d="M18.8 22.8c-1.6 0-2.7-1.3-2.3-2.8.3-1.2 1.5-2 2.3-3.2.8 1.2 2 2 2.3 3.2.4 1.5-.7 2.8-2.3 2.8z"
      />
    </>
  ),

  // The one that is only ours: the next rank, arriving.
  levelup: (
    <>
      <path className="dim" d="M5.4 21.4h13.2" />
      <path className="dim" d="M6 17.6l6-4.8 6 4.8" />
      <path d="M6 12.4l6-4.8 6 4.8" />
      <path className="g" d="M6 7.2L12 2.4l6 4.8" />
    </>
  ),

  /* --- Folklore --------------------------------------------------------- */

  // Explain the bug to it. The bug goes away. Nobody knows why.
  rubberduck: (
    <>
      <path
        className="ink"
        d="M15.6 3.6a4.2 4.2 0 0 0-3.7 6.2C7.4 10.4 4 12.9 4 15.9c0 2.9 3.6 4.7 8.4 4.7 4.6 0 8.4-2 8.4-5.2 0-2.2-1.5-3.9-3.7-4.8a4.2 4.2 0 0 0-1.5-6.9z"
      />
      <circle className="punchf" cx="16.4" cy="7.4" r="1" />
      <path className="gf" d="M19.4 6.2l3.8 1.4-3.8 1.7z" />
      <path className="dim" d="M2.6 21.8h18.8" />
    </>
  ),
  // Four hours in, and the task you started on is still untouched.
  yakshave: (
    <>
      <path d="M6.6 9.8C3.8 8.6 3.6 5 6.4 4" />
      <path d="M17.4 9.8c2.8-1.2 3-4.8.2-5.8" />
      <path d="M12 7.4c-3.6 0-6.2 2.6-6.2 6 0 3.4 2.6 5.8 6.2 5.8s6.2-2.4 6.2-5.8c0-3.4-2.6-6-6.2-6z" />
      <g className="ink">
        <circle cx="9.8" cy="12.4" r="1" />
        <circle cx="14.2" cy="12.4" r="1" />
      </g>
      <path d="M10 16.4h4" />
      <g className="g">
        <path d="M8.8 9.4l.8-2" />
        <path d="M12 8.9v-2" />
        <path d="M15.2 9.4l-.8-2" />
      </g>
    </>
  ),
  // Nobody has an opinion about the reactor. Everybody has one about the shed.
  bikeshed: (
    <>
      <path d="M2.4 10.6L12 4.2l9.6 6.4" />
      <path d="M5.2 11.8v7.6h13.6v-7.6" />
      <path className="dim" d="M10.2 19.4v-4.4h3.6v4.4" />
      <circle className="g" cx="18.8" cy="17.6" r="3.4" />
    </>
  ),
  // It does, though.
  worksonmine: (
    <>
      <ellipse className="g" cx="12" cy="5" rx="4.4" ry="1.7" />
      <rect x="5.4" y="8.4" width="13.2" height="8.4" rx="1.6" />
      <path className="dim" d="M8.4 11.6h5.4M8.4 13.8h3.2" />
      <path d="M2.6 19.6h18.8" />
    </>
  ),

  // It works. Nobody may touch it.
  spaghetti: (
    <>
      <path d="M3.4 13.4h17.2a8.6 8.6 0 0 1-17.2 0z" />
      <path className="dim" d="M6.4 13c-1.4-3.4 1.6-6 4-4.4s.4 4 2.6 4 3-3.4 1-4.6" />
      <path className="dim" d="M9.4 13c-2.2-2.6.6-6.2 3.4-4.8" />
      <path className="dim" d="M16 13c1.8-1.8 1.4-5-1-5.4" />
      <path className="g" d="M12.4 13c-1-2.4 1-4.6 3-3.8" />
    </>
  ),

  // Three of them. The loop found four.
  offbyone: (
    <>
      <g className="ink">
        <rect x="1.6" y="9.6" width="4.4" height="4.4" rx="1" />
        <rect x="7.2" y="9.6" width="4.4" height="4.4" rx="1" />
        <rect x="12.8" y="9.6" width="4.4" height="4.4" rx="1" />
      </g>
      <rect x="18.4" y="9.6" width="4.4" height="4.4" rx="1" strokeDasharray="1.8 1.6" />
      <path className="g" d="M20.6 3.6v3.2" />
      <path className="g" d="M18.8 5.2l1.8 1.8 1.8-1.8" />
    </>
  ),

  // Perfectly reproducible until the moment somebody watches.
  heisenbug: (
    <>
      <g strokeDasharray="1.8 1.8">
        <ellipse cx="9.4" cy="14" rx="4.4" ry="3.6" />
        <path d="M5 11.6L2.6 9.8M5 16.4l-2.4 1.8M9.4 10.4V7.6" />
      </g>
      <circle cx="15.4" cy="10.4" r="5" />
      <path d="M19.2 14l2.8 2.8" />
    </>
  ),

  // It only grows. It is always the one thing you did not write.
  memoryleak: (
    <>
      <rect x="5.4" y="2.6" width="13.2" height="10.4" rx="1.8" />
      <rect className="gf" x="7.2" y="8.2" width="9.6" height="3" rx="1.2" />
      <g className="gf">
        <path d="M12 15.2c.9 1.2 1.3 1.9 1.3 2.5a1.3 1.3 0 0 1-2.6 0c0-.6.4-1.3 1.3-2.5z" />
        <path d="M8.4 18.8c.7 1 1.1 1.5 1.1 2a1.1 1.1 0 0 1-2.2 0c0-.5.4-1 1.1-2z" />
        <path d="M15.6 19.6c.6.9.9 1.3.9 1.7a.9.9 0 0 1-1.8 0c0-.4.3-.8.9-1.7z" />
      </g>
    </>
  ),

  // Both of them arrive. Only one of them is right, and not the same one
  // twice.
  racecondition: (
    <>
      <path className="dim" d="M2.6 6.4h8.8" />
      <path d="M9 4.2l2.4 2.2-2.4 2.2" />
      <path className="dim" d="M2.6 17.6h8.8" />
      <path d="M9 15.4l2.4 2.2-2.4 2.2" />
      <path className="dim" d="M13 7.4l2.8 3M13 16.6l2.8-3" />
      <circle className="gf" cx="18.4" cy="12" r="2.8" />
      <g className="g">
        <path d="M22.4 8.6l-1.4 1.2" />
        <path d="M22.4 15.4l-1.4-1.2" />
      </g>
    </>
  ),

  // After you. No, after you.
  deadlock: (
    <>
      <path className="dim" d="M5.6 8.2a7.4 7.4 0 0 1 12.8 0" />
      <path d="M16.2 4.6l2.4 3.4-4 .8" />
      <path className="dim" d="M18.4 15.8a7.4 7.4 0 0 1-12.8 0" />
      <path d="M7.8 19.4l-2.4-3.4 4-.8" />
      <rect x="9" y="11.4" width="6" height="5" rx="1.2" />
      <path d="M10.4 11.4v-1.2a1.6 1.6 0 0 1 3.2 0v1.2" />
    </>
  ),

  // Borrowed on Tuesday. The interest is every Tuesday after.
  techdebt: (
    <>
      <rect x="4" y="17.2" width="16" height="3.6" rx="1" />
      <rect x="5.4" y="12.8" width="13.2" height="3.6" rx="1" transform="rotate(-3 12 14.6)" />
      <rect x="6.8" y="8.4" width="11" height="3.6" rx="1" transform="rotate(-8 12 10.2)" />
      <rect
        className="g"
        x="8.4"
        y="4"
        width="9"
        height="3.6"
        rx="1"
        transform="rotate(-15 12 5.8)"
      />
    </>
  ),

  // The number of people who can be hit by a bus before the project stops.
  // In most projects it is one, and everybody knows which one.
  busfactor: (
    <>
      <rect x="2.4" y="5.6" width="19.2" height="10.8" rx="2.2" />
      <g className="dim">
        <rect x="5" y="8.4" width="5.4" height="3.6" rx="0.8" />
        <rect x="13" y="8.4" width="5.4" height="3.6" rx="0.8" />
      </g>
      <g className="ink">
        <circle cx="7" cy="18.4" r="1.9" />
        <circle cx="17" cy="18.4" r="1.9" />
      </g>
      <circle className="gf" cx="19.4" cy="13.8" r="1.1" />
    </>
  ),

  // Straight to production, partner.
  cowboycode: (
    <>
      <path d="M7 8.6c-.6-3.6.8-5.4 5-5.4s4.8 1.8 4.2 5.4" />
      <path d="M2.6 8.8c3.2-2 15.6-2 18.8 0" />
      <rect x="3.6" y="10.4" width="16.8" height="9.8" rx="2.4" />
      <g className="ink">
        <circle cx="9" cy="14.4" r="1.1" />
        <circle cx="15" cy="14.4" r="1.1" />
      </g>
      <path className="g" d="M9.4 17.2q2.6 1.8 5.2 0" />
    </>
  ),

  // Sent from inside. Nobody has heard from them since.
  exitvim: (
    <>
      <rect x="2.4" y="6.2" width="19.2" height="11.6" rx="2.2" />
      <text x="10.4" y="14.4" textLength="10.4" lengthAdjust="spacingAndGlyphs">
        :q!
      </text>
      <rect className="gf ce-blink" x="16.4" y="9.8" width="2.8" height="4.6" />
    </>
  ),

  // The debugger everybody actually uses.
  printf: (
    <>
      <rect x="2.6" y="4.6" width="18.8" height="14.8" rx="2.4" />
      <g className="dim">
        <path d="M5.8 9h7.4" />
        <path d="M5.8 12h9.6" />
      </g>
      <path className="g" d="M5.8 15.2h5.2" />
      <ellipse className="ink" cx="15.4" cy="15.2" rx="1.9" ry="1.5" />
      <g className="ink">
        <path d="M13.4 13.8l-1.2-.8M13.4 16.6l-1.2.8M17.4 13.8l1.2-.8M17.4 16.6l1.2.8" strokeWidth="1" />
      </g>
    </>
  ),

  // The oldest merge conflict there is.
  tabsvsspaces: (
    <>
      <path className="dim" d="M2.4 12h5.2" />
      <path className="dim" d="M5.6 9.8L7.8 12l-2.2 2.2" />
      <path className="dim" d="M8.6 9v6" />
      <path d="M12.6 3.4l-1.8 2.8 1.8 2.8-1.8 2.8 1.8 2.8-1.8 2.8 1.8 2.6" />
      <g className="ink">
        <circle cx="16.4" cy="12" r="1.1" />
        <circle cx="19.2" cy="12" r="1.1" />
        <circle cx="22" cy="12" r="1.1" />
      </g>
    </>
  ),

  /* --- Expressions ------------------------------------------------------ */

  // The room is on fire and the standup is in ten minutes.
  thisisfine: (
    <>
      {SCREEN}
      {EYES}
      <path d="M9.4 15.4h5.2" />
      <g className="gf">
        <path d="M4.8 22.6c-1.7 0-2.8-1.4-2.4-3 .3-1.3 1.6-2.2 2.4-3.5.8 1.3 2.1 2.2 2.4 3.5.4 1.6-.7 3-2.4 3z" />
        <path d="M19.2 22.6c-1.7 0-2.8-1.4-2.4-3 .3-1.3 1.6-2.2 2.4-3.5.8 1.3 2.1 2.2 2.4 3.5.4 1.6-.7 3-2.4 3z" />
      </g>
    </>
  ),

  // Not a stack trace. A stack.
  panic: (
    <>
      {SCREEN}
      <path d="M7 9.4l2.8 2.8M9.8 9.4L7 12.2" />
      <path d="M14.2 9.4l2.8 2.8M17 9.4l-2.8 2.8" />
      <path d="M8.4 16.4l1.6-1.7 1.6 1.7 1.6-1.7 1.6 1.7" />
    </>
  ),

  // Sent instead of a reply.
  stare: (
    <>
      {SCREEN}
      <circle cx="8.6" cy="11" r="2.1" />
      <circle cx="15.4" cy="11" r="2.1" />
      <g className="ink">
        <circle cx="8.6" cy="11" r="0.85" />
        <circle cx="15.4" cy="11" r="0.85" />
      </g>
      <path d="M10.9 16.2h2.2" />
    </>
  ),

  // It is 17:40 on a Friday and the deploy is green so far.
  sweating: (
    <>
      {SCREEN}
      {EYES}
      <path d="M8.8 15.8q1.3-1.4 2.6 0t2.6 0" />
      <path className="gf" d="M18.4 6.4c1.1 1.5 1.6 2.3 1.6 3a1.6 1.6 0 0 1-3.2 0c0-.7.5-1.5 1.6-3z" />
    </>
  ),

  // Said nothing in the design review. Says something now.
  smug: (
    <>
      {SCREEN}
      <path d="M7.2 11.4q1.4-1.8 2.8 0" />
      <path d="M14 11.4q1.4-1.8 2.8 0" />
      <path d="M8.8 15.2q2.8 1.8 5.4-1" />
    </>
  ),

  // Same bug. Same file. Same week.
  sigh: (
    <>
      {SCREEN}
      <path d="M7.2 10.8h2.8" />
      <path d="M14 10.8h2.8" />
      <path d="M9.6 15.6h3.2" />
      <g className="dim">
        <path d="M15 16.8c1.2-.2 2-.9 2.3-1.9" />
        <path d="M17.8 18c1.5-.3 2.5-1.2 2.9-2.6" />
      </g>
    </>
  ),

  // Nobody knows why it works either. Not `:shrug:` — Unicode already owns
  // that shortcode, and a collision would mean a code that expands to 🤷 on
  // one build and draws this on the next (the protocol test enforces it).
  noidea: (
    <>
      <rect x="6.4" y="5.2" width="11.2" height="11.4" rx="2.6" />
      <g className="ink">
        <circle cx="9.6" cy="10" r="1" />
        <circle cx="14.4" cy="10" r="1" />
      </g>
      <path d="M10.2 13.4h3.6" />
      <path d="M6 13.2l-2.8 1.4M3.2 14.6l-.5-2.6" />
      <path d="M18 13.2l2.8 1.4M20.8 14.6l.5-2.6" />
    </>
  ),

  // Third coffee, second hour past midnight, one line left to fix.
  wired: (
    <>
      {SCREEN}
      <circle cx="8.6" cy="10.8" r="2.3" />
      <circle cx="15.4" cy="10.8" r="2.3" />
      <g className="ink">
        <circle cx="8.6" cy="10.8" r="1" />
        <circle cx="15.4" cy="10.8" r="1" />
      </g>
      <path d="M8.6 16.2h6.8" />
      <g className="g">
        <path d="M1 9.6v4.8" />
        <path d="M23 9.6v4.8" />
      </g>
    </>
  ),

  // The one that jumps. Every product with a chat room ends up with one of
  // these; ours is the mark, because the mark is what we have.
  partycaret: (
    <>
      <path d="M6 9.2L10 12.2L6 15.2" strokeWidth="2.2" />
      <rect className="gf ce-jump" x="12.8" y="7.6" width="4" height="9" />
      <g className="dim ce-pop">
        <path d="M20.4 6.2v1.8" />
        <path d="M3.6 5.4l1.2 1.4" />
        <path d="M21 14.6h1.8" />
        <path d="M19.4 19.4l1.4-1" />
      </g>
    </>
  ),

  // The same bug, for the third time, in the file you wrote.
  headdesk: (
    <>
      <g transform="rotate(-16 12 19)">
        <rect x="4.2" y="7.4" width="15.6" height="11.6" rx="2.8" />
        <path d="M7.6 12.4h2.6" />
        <path d="M13.8 12.4h2.6" />
        <path d="M10.2 15.8h3.6" />
      </g>
      <path d="M2.6 20.4h18.8" />
      <g className="dim">
        <path d="M4.4 17.6l-1.4-1.4" />
        <path d="M19.6 17.6l1.4-1.4" />
      </g>
    </>
  ),

  // It compiled first time and you are going to hear about it.
  shades: (
    <>
      {SCREEN}
      <g className="ink">
        <path d="M4.6 9.2h6v2.8a2 2 0 0 1-2 2H6.6a2 2 0 0 1-2-2z" />
        <path d="M13.4 9.2h6v2.8a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z" />
      </g>
      <path className="ink" d="M10.6 10h2.8v1h-2.8z" />
      <path className="g" d="M5.8 10.2l1.6 1.4" />
      <path d="M8.8 16.6q3 1.8 5.8-.8" />
    </>
  ),

  // Read it twice. Said nothing. Looked over.
  sideeye: (
    <>
      {SCREEN}
      <circle cx="8.6" cy="11" r="2.1" />
      <circle cx="15.4" cy="11" r="2.1" />
      <g className="ink">
        <circle cx="9.9" cy="11.2" r="0.9" />
        <circle cx="16.7" cy="11.2" r="0.9" />
      </g>
      <path d="M10.4 16.2q2.2-1 4.2 0" />
    </>
  ),

  // Has already pushed it.
  mischief: (
    <>
      {SCREEN}
      <path d="M3.6 4.2l2.2 2.4" />
      <path d="M20.4 4.2l-2.2 2.4" />
      <path d="M6.8 10l2.8 1.6" />
      <path d="M17.2 10l-2.8 1.6" />
      <path d="M8 15q4 3.2 8 0" />
      <path className="g" d="M9.8 15.8v1.6M14.2 15.8v1.6" strokeWidth="1.2" />
    </>
  ),

  // Somebody else's pull request, and it is beautiful.
  smitten: (
    <>
      {SCREEN}
      <g className="gf">
        <path d="M8.6 13.4c-2.6-1.9-3.1-3.5-2-4.3.8-.6 1.6-.1 2 .5.4-.6 1.2-1.1 2-.5 1.1.8.6 2.4-2 4.3z" />
        <path d="M15.4 13.4c-2.6-1.9-3.1-3.5-2-4.3.8-.6 1.6-.1 2 .5.4-.6 1.2-1.1 2-.5 1.1.8.6 2.4-2 4.3z" />
      </g>
      <path d="M9 16q3 2 6-.4" />
    </>
  ),

  // The build is green, the deploy is out, and it is only Tuesday.
  hyped: (
    <>
      {SCREEN}
      <g className="gf">
        <path d="M8.6 7.8l.9 2 2 .9-2 .9-.9 2-.9-2-2-.9 2-.9z" />
        <path d="M15.4 7.8l.9 2 2 .9-2 .9-.9 2-.9-2-2-.9 2-.9z" />
      </g>
      <path className="ink" d="M8.6 15.2h6.8a3.4 3.4 0 0 1-6.8 0z" />
    </>
  ),

  // It was in production. It was in production the whole time.
  screaming: (
    <>
      {SCREEN}
      {EYES}
      <ellipse className="ink" cx="12" cy="15.8" rx="2.4" ry="2" />
      <g className="dim">
        <path d="M2.6 2.8l1.6 1.6" />
        <path d="M21.4 2.8l-1.6 1.6" />
      </g>
    </>
  ),

  // Went to make coffee. That was some time ago.
  afk: (
    <>
      {SCREEN}
      <path d="M6.4 12.4h2.8" />
      <path d="M11.4 12.4h2.8" />
      <path d="M8.4 16h3.6" />
      <path className="g" d="M15.4 7h3.8l-3.8 4.4h3.8" />
    </>
  ),

  // The face of somebody who has been reading a stack trace for an hour.
  brain404: (
    <>
      {SCREEN}
      <text x="12" y="14.6" textLength="13" lengthAdjust="spacingAndGlyphs">
        404
      </text>
    </>
  ),

};

/**
 * Which of them have a drawing.
 *
 * Exported for the test that holds the two halves together: the roster lives
 * in the protocol package and the pictures live here, so nothing but a test
 * stops somebody adding a row and forgetting the drawing, or deleting a row
 * and leaving the drawing behind.
 */
export const DRAWN: readonly string[] = Object.keys(ART);

/* ── The stickers ───────────────────────────────────────────────────────── */

/**
 * The same chassis, twice the size.
 *
 * `SCREEN` and `EYES` doubled, unit for unit, so a sticker's face and an
 * emoji's face are the same face. The Expressions group's rule holds here and
 * holds harder: at four times the area a yellow circle would be four times as
 * much somebody else's product. A face in this file is a terminal.
 */
const SCREEN_L = <rect x="5.2" y="9.2" width="37.6" height="29.6" rx="6.4" />;

/** The same two dots, doubled. */
const EYES_L = (
  <g className="ink">
    <circle cx="17.2" cy="22" r="2.3" />
    <circle cx="30.8" cy="22" r="2.3" />
  </g>
);

/**
 * Shortcode to artwork, for the twelve that are sent instead of a sentence.
 *
 * Two things differ from `ART` and only two. The box is 48 units instead of
 * 24, which is where the detail goes — a crate can have a ground under it and
 * a trail behind it, a rebase can show the tangle *and* the line it became.
 *
 * And the stroke is 2.4, not the 3.2 that a straight scale of 1.6 would give.
 * A line weight is not a property of the drawing, it is a property of how far
 * away the drawing is read from, and these are read from the same distance as
 * everything else: a bigger picture wants a relatively finer line, or it turns
 * into a logo. 3.2 on 48 units is the same optical thickness as 1.6 on 24 and
 * it looks bludgeoned; 2.4 is about three quarters of that, which is where the
 * detail above stops closing up and the whole thing still reads at the 23px a
 * picker cell gives it.
 *
 * Everything else is the house style, unchanged: line art, `currentColor`,
 * round caps, fills only through `.ink` / `.gf` / `.punchf`, `.dim` for what
 * is behind the subject, green only on what is live or good, and no red —
 * `deadbug` is a dead beetle drawn in ink, the same way `redbuild` is a hollow
 * ring.
 */
const STICKER_ART: Record<string, ReactNode> = {
  // The crate is down, the tracks behind it are cooling, and somebody has
  // already put a flag in it.
  shipped: (
    <>
      <g className="dim">
        <path d="M1.6 25h9" />
        <path d="M1 31.2h10" />
        <path d="M3.6 37h7" />
      </g>
      <rect x="14" y="21" width="26" height="19.6" rx="3" />
      <path className="dim" d="M14 27.2h26" />
      <path d="M27 21V6" />
      <path className="gf" d="M27 6.6l11.2 3.8L27 14.2z" />
      <path d="M3.6 42.4h40.8" />
    </>
  ),

  // Not one build. The suite.
  allgreen: (
    <>
      <rect x="3.6" y="7.4" width="40.8" height="30.4" rx="4" />
      <path className="dim" d="M3.6 13.6h40.8" />
      <g className="g">
        <path d="M9 20.6l2.9 3.1 5.1-6.3" />
        <path d="M20 20.6l2.9 3.1 5.1-6.3" />
        <path d="M31 20.6l2.9 3.1 5.1-6.3" />
        <path d="M9 31.1l2.9 3.1 5.1-6.3" />
        <path d="M20 31.1l2.9 3.1 5.1-6.3" />
        <path d="M31 31.1l2.9 3.1 5.1-6.3" />
      </g>
      <path d="M24 37.8v4.2" />
      <path d="M17 42h14" />
    </>
  ),

  // It came up on its own, and the filament is the part nobody wrote.
  itsalive: (
    <>
      <g className="dim">
        <path d="M10.6 5.6l2.6 2.4" />
        <path d="M37.4 5.6l-2.6 2.4" />
        <path d="M6.6 14.6h3.4" />
        <path d="M38 14.6h3.4" />
      </g>
      <circle cx="24" cy="13.4" r="7" />
      <path className="g" d="M20.6 14.4c1-3.6 2.2-3.6 3.4 0s2.4 3.6 3.4 0" />
      <path d="M20.8 21.6h6.4" />
      <path d="M21.6 24.2h4.8" />
      <rect x="6" y="26.8" width="36" height="16.8" rx="3.4" />
      <path d="M12.6 32.2l4.4 3.6-4.4 3.6" />
      <rect className="gf ce-blink" x="21.4" y="31.6" width="3.6" height="8.4" />
    </>
  ),

  // On its back, six legs in the air. Squashed, not in progress.
  deadbug: (
    <>
      <path d="M18.1 18.6L11.4 15.2 9.8 10.4" />
      <path d="M15.6 24.6L7.6 23 6 18.2" />
      <path d="M18.1 30.6L10.4 31 8.4 26.2" />
      <path d="M29.9 18.6l6.7-3.4 1.6-4.8" />
      <path d="M32.4 24.6l8-1.6 1.6-4.8" />
      <path d="M29.9 30.6l7.7.4 2-4.8" />
      <ellipse cx="24" cy="24.6" rx="8.4" ry="8.4" />
      <g className="dim">
        <path d="M17.4 21.6h13.2" />
        <path d="M16.4 27h15.2" />
      </g>
      <circle cx="24" cy="39" r="6" />
      <g strokeWidth="1.5">
        <path d="M19.2 37l3.2 3.2M22.4 37l-3.2 3.2" />
        <path d="M25.6 37l3.2 3.2M28.8 37l-3.2 3.2" />
      </g>
    </>
  ),

  // The rebase, before and after, in one frame.
  combedout: (
    <>
      {/* The before is a graph and not a scribble, because this product draws
          git graphs all day and a learner reads one faster than they read a
          knot: two lanes, commits landing in both, and edges crossing between
          them. Straight segments rather than curves — an organic arc at this
          weight reads as a limb, which is what the first attempt at this
          drawing turned out to be. */}
      <path className="dim" d="M7 10.4v27.2" />
      <path className="dim" d="M18.4 15.6v16.4" />
      <path d="M7 10.4l11.4 5.2" />
      <path d="M18.4 15.6L7 23.8" />
      <path d="M7 23.8l11.4 8.2" />
      <path d="M18.4 32L7 37.6" />
      <g className="ink">
        <circle cx="7" cy="10.4" r="2.4" />
        <circle cx="18.4" cy="15.6" r="2.4" />
        <circle cx="7" cy="23.8" r="2.4" />
        <circle cx="18.4" cy="32" r="2.4" />
        <circle cx="7" cy="37.6" r="2.4" />
      </g>
      <g className="dim">
        <path d="M24 24h5.6" />
        <path d="M27 21.4l2.6 2.6-2.6 2.6" />
      </g>
      <path className="dim" d="M38 12.8v25.2" />
      <g className="ink">
        <circle cx="38" cy="21.4" r="2.8" />
        <circle cx="38" cy="29.6" r="2.8" />
        <circle cx="38" cy="37.8" r="2.8" />
      </g>
      <circle className="gf" cx="38" cy="12.8" r="3.6" />
    </>
  ),

  // Heavy lids, a moon, and a mug that has nothing left to give off.
  smallhours: (
    <>
      <rect x="3.4" y="9.6" width="27" height="25.2" rx="5.2" />
      <path d="M7.1 20.4h7.6" />
      <path className="ink" d="M7.9 20.4a3.5 3.5 0 0 0 7 0z" />
      <path d="M18.1 20.4h7.6" />
      <path className="ink" d="M18.9 20.4a3.5 3.5 0 0 0 7 0z" />
      <path d="M13.4 28.6h7" />
      <path d="M45 11.6A7 7 0 1 1 37.4 4 5.5 5.5 0 0 0 45 11.6z" />
      <path d="M32.6 26.6h11v7.2a3.6 3.6 0 0 1-3.6 3.6h-3.8a3.6 3.6 0 0 1-3.6-3.6z" />
      <path d="M43.6 28.8h1.4a2.6 2.6 0 0 1 0 5.2h-1.4" />
      <path className="dim" d="M31 40.6h14" />
    </>
  ),

  // It comes down on the diff, and the sheet keeps the mark.
  stamped: (
    <>
      <rect x="2.6" y="23.4" width="42.8" height="21.6" rx="2.6" />
      <g className="dim">
        <path d="M24.6 38.4h13.4" />
        <path d="M24.6 42h9" />
      </g>
      <g className="g" transform="rotate(-9 14 34)">
        <circle cx="14" cy="34" r="7.4" />
        <path d="M10.2 34.2l2.8 3 5.2-6.4" />
      </g>
      <g transform="rotate(8 34 24)">
        <rect x="25.4" y="27.4" width="17.2" height="6.4" rx="2" />
        <rect x="31.4" y="20" width="5.2" height="7.4" />
        <rect x="27.4" y="13.4" width="13.2" height="6.6" rx="3.2" />
      </g>
    </>
  ),

  // One commit, and nothing on either side of it yet. The empty half of the
  // frame is the half that is the point.
  dayone: (
    <>
      <g className="dim" strokeDasharray="2 3.6">
        <path d="M4.4 32h12" />
        <path d="M31.6 32h12" />
      </g>
      <path d="M24 26.6V8.4" />
      <path d="M24 9.4h10.2l-2.8 3.4 2.8 3.4H24" />
      <circle className="gf" cx="24" cy="32" r="5.4" />
    </>
  ),

  // The spinner has been going a while. So has the stare.
  stuckhere: (
    <>
      <path className="dim" d="M28.2 5.8a4.2 4.2 0 1 1-1.3-3" />
      <g transform="translate(0 3.8)">
        {SCREEN_L}
        {EYES_L}
        <path d="M16.4 31h15.2" />
      </g>
    </>
  ),

  // The steam comes back round and points at the cup. Another one.
  coffeeloop: (
    <>
      <path d="M11.4 22.2c1.8-3 .6-4.8 1.4-8.2a5.8 5.8 0 0 1 11.6 0v6.4" />
      <path d="M21.4 17.4L24.4 20.6 27.4 17.4" />
      <path d="M7.6 22.4h22v12.6a6.6 6.6 0 0 1-6.6 6.6h-8.8a6.6 6.6 0 0 1-6.6-6.6z" />
      <path className="dim" d="M9.4 26.6h18.4" />
      <path d="M29.6 25.4h3.8a5.4 5.4 0 0 1 0 10.8h-3.8" />
      <path className="dim" d="M4.4 43.6h30" />
    </>
  ),

  // The lever is live and the hand is in the way. No day on it anywhere: the
  // English name says Friday and the Arabic says Thursday, and the picture
  // has to be true in both.
  notfriday: (
    <>
      <g className="dim">
        <rect x="24.6" y="33.4" width="17.4" height="8.6" rx="2.4" />
        <path d="M28.4 37.8h9" />
      </g>
      <path d="M33 37.8L36 29l2.4-5" />
      <circle cx="33" cy="37.8" r="1.5" />
      <circle className="gf" cx="39.4" cy="21.4" r="3.4" />
      <path d="M11.4 27.4V18.6" />
      <path d="M15.6 27.4V15.4" />
      <path d="M19.8 27.4V15" />
      <path d="M24 27.4V17.6" />
      <path d="M9.4 29L4.8 24.2" />
      <path d="M8.6 28h17.8v6.6a7.4 7.4 0 0 1-7.4 7.4h-3a7.4 7.4 0 0 1-7.4-7.4z" />
    </>
  ),

  // A rosette with a terminal in the middle of it, because the thing being
  // praised is somebody else's work on the machine.
  welldone: (
    <>
      <path d="M19.6 29.2L15.6 43.6l5.6-3.2 4.2 3.2L28 29.2" />
      <g className="dim">
        <path d="M34.6 19h3.2" />
        <path d="M33.2 13.7l2.8-1.6" />
        <path d="M29.3 9.8l1.6-2.8" />
        <path d="M24 8.4V5.2" />
        <path d="M18.7 9.8l-1.6-2.8" />
        <path d="M14.8 13.7l-2.8-1.6" />
        <path d="M13.4 19h-3.2" />
        <path d="M14.8 24.3l-2.8 1.6" />
        <path d="M33.2 24.3l2.8 1.6" />
      </g>
      <circle cx="24" cy="19" r="10.6" />
      <rect x="15.8" y="13.8" width="16.4" height="10.8" rx="2.8" />
      <path d="M18.8 17l3 2.6-3 2.6" />
      <rect className="gf" x="24.4" y="16.4" width="2.8" height="5.8" />
    </>
  ),
};

/**
 * Which of the stickers have a drawing. The twin of {@link DRAWN}, and the
 * other half of the same test: the roster is in the protocol package, the
 * pictures are here, and nothing but a test stops the two drifting apart.
 */
export const STICKERS_DRAWN: readonly string[] = Object.keys(STICKER_ART);

/* ── Drawing one ────────────────────────────────────────────────────────── */

export interface CustomGlyphProps {
  emoji: CustomEmoji;
  /** The picker draws the name beside the cell already; a message does not. */
  labelled?: boolean;
}

export function CustomGlyph({ emoji, labelled = true }: CustomGlyphProps) {
  // The name is the only thing a screen reader gets — the drawing is an
  // `aria-hidden` SVG — so it is the one place on these fifty-eight emoji
  // where leaving the English would mean the joke is simply not delivered.
  const { locale } = useLocale();
  const art = ART[emoji.code];
  // A roster entry nobody has drawn yet: show what was actually sent.
  if (!art) return <span className="ce-raw">{emoji.c}</span>;
  return (
    <span
      className={`ce ce-${emoji.code}`}
      role={labelled ? 'img' : undefined}
      aria-label={labelled ? customName(emoji, locale) : undefined}
      aria-hidden={labelled ? undefined : true}
      title={emoji.c}
    >
      <svg
        className="ce-art"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {art}
      </svg>
    </span>
  );
}

/**
 * One sticker.
 *
 * `CustomGlyph`'s twin rather than a prop on it, because the two things that
 * differ between them are the two things an `<svg>` element decides: the box
 * the drawing was authored in and the weight of the line in it. A single
 * component that took both as parameters would be a component whose whole body
 * was a conditional, and every caller would have to know which branch it
 * wanted anyway.
 *
 * The `<svg>` carries `ce-art` as well as `ce-sticker-art` on purpose. The part
 * classes — `.g`, `.gf`, `.ink`, `.dim`, `.punch`, and the reduced-motion list
 * — are declared against `.ce-art` and mean exactly the same thing at either
 * size, so a sticker inherits all of them and the stylesheet only has to say
 * what actually differs.
 */
export function StickerGlyph({ emoji, labelled = true }: CustomGlyphProps) {
  const { locale } = useLocale();
  const art = STICKER_ART[emoji.code];
  if (!art) return <span className="ce-raw">{emoji.c}</span>;
  return (
    <span
      className={`ce-sticker ce-sticker-${emoji.code}`}
      role={labelled ? 'img' : undefined}
      aria-label={labelled ? customName(emoji, locale) : undefined}
      aria-hidden={labelled ? undefined : true}
      title={emoji.c}
    >
      <svg
        className="ce-art ce-sticker-art"
        viewBox="0 0 48 48"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {art}
      </svg>
    </span>
  );
}

/**
 * A message, with ours drawn and everything else left as the text it is.
 *
 * The common row has none of ours in it and comes back from `splitCustom` as
 * the single string it went in as, which is what this renders — no elements,
 * no keys, nothing. Text is still only ever text: the only thing that becomes
 * markup is an exact shortcode that matches a drawing this file holds, and
 * there is no path from a message body to arbitrary HTML.
 */
export function RichText({ text }: { text: string }) {
  const parts = splitCustom(text);
  if (parts.length === 1 && 'text' in parts[0]!) return <>{text}</>;
  return (
    <>
      {parts.map((part, i) =>
        'text' in part ? (
          <span key={i}>{part.text}</span>
        ) : part.emoji.sticker ? (
          // A sticker inside a sentence — `:shipped: nice` — is still a
          // sticker and still has to be drawn, but it is not the message, so
          // it is set at word size (emoji-custom.css). The row that *is* one
          // is what `loneSticker` finds, and the message list is what decides
          // it gets the whole width.
          <StickerGlyph key={i} emoji={part.emoji} />
        ) : (
          <CustomGlyph key={i} emoji={part.emoji} />
        ),
      )}
    </>
  );
}

export default CustomGlyph;
